import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import getEncryptionCerts from '../services/getEncryptionCerts';
import GetCertsResponse from 'owa-service/lib/contract/GetCertsResponse';
import showMissingCertificateDialog from '../components/missingCertificateDialog/MissingCertificateDialog';
import { DialogResponse } from 'owa-confirm-dialog';
import uniq from 'lodash-es/uniq';
import { CANCEL_SMIME_SEND_ERROR_MESSAGE } from './constants';
import SmimeContext from '../store/schema/SmimeContext';
import SmimeCustomError from './SmimeCustomError';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';
import { trace } from 'owa-trace';

/**
 * This function makes a service call and returns
 * encryption certificates, if every recipient has valid certificates
 * Otherwise displays a dialog with invalid recipients to perform necessary action
 * @param recipients Recipients for which certificates need to be fetched.
 */
export default async function getEncryptionCertificates(
    recipients: EmailAddressWrapper[]
): Promise<string[]> {
    const getCertsResponse: GetCertsResponse = await getEncryptionCerts(recipients);

    if (!getCertsResponse || getCertsResponse?.ErrorStatus) {
        getCertsResponse &&
            trace.error(
                'SmimeTraceError: An error occurred while fetching encryption certificates. ErrorStatus: ' +
                    getCertsResponse.ErrorStatus
            );
        throw new SmimeCustomError(
            'S/MIME: Unable to fetch encryption certificates',
            SmimeErrorStateEnum.WarningSmimeCannotGetCerts,
            SmimeContext.EncryptingMessage
        );
    }

    const { InvalidRecipients, ValidRecipients } = getCertsResponse;
    // Display missing certificate dialog if invalid recipients are present.
    if (InvalidRecipients && InvalidRecipients.length > 0) {
        const response: DialogResponse = await showMissingCertificateDialog(
            flattenGetEncryptionCertsResponse(InvalidRecipients)
        );
        if (response == DialogResponse.ok) {
            return await getEncryptionCertificates(recipients);
        } else if (response == DialogResponse.cancel) {
            /**
             * In case of cancel, we will throw generic error with following
             * error message, and we will skip adding infobar id into composeViewState
             * as we need to return to compose silently.
             */
            throw new Error(CANCEL_SMIME_SEND_ERROR_MESSAGE);
        }
    }

    // Return certificates of valid recipients.
    return ValidRecipients && ValidRecipients.length > 0
        ? flattenGetEncryptionCertsResponse(ValidRecipients)
        : null;
}

/**
 * This util will parse the fields of getEncryptionCertsResponse
 * which are two-dimensional arrays and return the unique values in
 * a single dimensional array removing the null values.
 * TODO: WI #43097 Replace the type of parameter to T[][] from any.
 * @param data The field of getEncryptionCertsResponse to be parsed
 */
export function flattenGetEncryptionCertsResponse<T>(data: any): T[] {
    return uniq(
        data.reduce((result, value) => {
            if (!!value) {
                result.push(...value);
            }
            return result;
        }, [])
    );
}
